import { userApis } from '@/configs/apis/user';
import { useRefs } from '@/scripts/hooks';
import { getDeviceId } from '@/pages/login/helpers';
import type { UserSettings, UserSettingsContent, UserSettingsParams } from './types';
import { genUserSettings } from './utils';

type userSettingsActions =
  | {
      type: 'setUserSettings';
      payload: UserSettingsParams;
    }
  | {
      type: 'getMsgExpireDuration';
      payload: number | undefined;
      cb: (time: number) => void;
    };

function reducer(state: UserSettings, action: userSettingsActions): UserSettings {
  let statePartial: UserSettingsParams | undefined;

  switch (action.type) {
    case 'setUserSettings':
      statePartial = action.payload;
      break;
    case 'getMsgExpireDuration':
      action.cb(state.expire_duration);
      break;
  }

  const state_ = statePartial ? { ...state, ...statePartial } : state;

  return state_;
}

const initialState = genUserSettings();

export function useUserSettingsState() {
  const [state, dispatch] = useReducer(reducer, initialState);

  const setUserSettings = useCallback((params: UserSettingsParams) => {
    dispatch({ type: 'setUserSettings', payload: params });
  }, []);

  const getMsgExpireDuration = useCallback((time?: number) => {
    return new Promise<number>((resolve) => {
      dispatch({ type: 'getMsgExpireDuration', payload: time, cb: resolve });
    });
  }, []);

  const value = useMemo(
    () => ({
      ...state,
      setUserSettings,
      getMsgExpireDuration,
    }),
    [state, setUserSettings, getMsgExpireDuration]
  );

  return value;
}

export function useUserSettings() {
  const userSettingsState = useUserSettingsState();

  const refs = useRefs({
    userSettingsState,
  });

  const updateUserSettings = useCallback(
    async (params: UserSettingsParams) => {
      const { userSettingsState } = refs.current;
      userSettingsState.setUserSettings(params);
    },
    [refs]
  );

  const setUserSettings = useCallback(
    async (params: UserSettingsParams) => {
      try {
        await userApis.setUserSettings(params);
        updateUserSettings(params);
      } catch (error) {
        return Promise.reject(error);
      }
    },
    [updateUserSettings]
  );

  const updateUserSettingsContent = useCallback(
    async (content?: UserSettingsContent) => {
      if (content?.device_id === getDeviceId()) {
        return;
      }
      const settings = await userApis.getUserSettings(content?.keys);
      updateUserSettings(settings);
    },
    [updateUserSettings]
  );

  const value = useMemo(
    () => ({
      userSettingsState,
      getMsgExpireDuration: userSettingsState.getMsgExpireDuration,
      updateUserSettings,
      setUserSettings,
      updateUserSettingsContent,
    }),
    [userSettingsState, updateUserSettings, setUserSettings, updateUserSettingsContent]
  );

  return value;
}
